import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { iconPositions } from '../../../../_constants/input.constant';

@Component({
    selector: 'lokos-input',
    templateUrl: './input.component.html',
    styleUrl: './input.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class InputComponent implements OnChanges, OnInit {
    @Input({ required: false }) control: FormControl = new FormControl('');
    @Input({ required: false }) disabled = false;
    @Input({ required: false }) iconPath = '';
    @Input({ required: false }) iconPosition = '';
    @Input({ required: false }) isAmount = false;
    @Input({ required: false }) allowZero = false;
    @Input({ required: false }) isIcon = false;
    @Input({ required: false }) label = '';
    @Input({ required: false }) maxLength!: number;
    @Input({ required: false }) maxValue!: number;
    @Input({ required: false }) minLength!: number;
    @Input({ required: false }) minValue!: number;
    @Input({ required: false }) readonly = false;
    @Input({ required: false }) value = '';
    @Input({ required: true }) placeholder = 'placeholder';
    @Input({ required: true }) required = false;
    @Input({ required: true }) type = 'text';
    @Output() afterBlur = new EventEmitter();
    @Output() afterFocus = new EventEmitter();
    @Output() afterIconClick = new EventEmitter();
    @Output() afterInput = new EventEmitter();
    @Output() keyPressEvent = new EventEmitter();

    iconPositions = iconPositions;
    /** Formatted display value for amount mode */
    displayValue = '';
    private isAmountFocused = false;

    /** Show mat-error as soon as control is dirty or touched */
    matcher: ErrorStateMatcher = {
        isErrorState: (control: FormControl | null): boolean => {
            return !!(
                control &&
                control.invalid &&
                (control.dirty || control.touched)
            );
        },
    };

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // Subscribe to control status changes to trigger error state re-evaluation with OnPush
        this.control.statusChanges.subscribe(() => {
            this.cdr.markForCheck();
        });

        if (this.isAmount) {
            this.applyNonZeroValidator();
            this.displayValue = this.formatIndianNumber(this.control.value);
            // Listen for external setValue/patchValue calls
            this.control.valueChanges.subscribe((val) => {
                if (!this.isAmountFocused) {
                    this.displayValue = this.formatIndianNumber(val);
                    this.cdr.markForCheck();
                }
            });
        }
    }

    private applyNonZeroValidator(): void {
        if (this.allowZero) return;
        this.control.addValidators(InputComponent.nonZeroAmountValidator);
        this.control.updateValueAndValidity({ emitEvent: false });
    }

    static nonZeroAmountValidator(
        control: AbstractControl
    ): ValidationErrors | null {
        const value = control.value;
        if (value === null || value === undefined || value === '') return null;
        const num = Number(String(value).replace(/,/g, ''));
        return !isNaN(num) && num === 0 ? { nonZeroAmount: true } : null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['disabled'] || changes['readonly']) {
            if (this.disabled || this.readonly) {
                this.control.disable({ emitEvent: false });
            } else {
                this.control.enable({ emitEvent: false });
            }
        }
        if (changes['value']) {
            this.control.patchValue(this.value);
        }

        if (changes['control'] && this.isAmount) {

            this.applyNonZeroValidator();
            this.displayValue = this.formatIndianNumber(this.control.value);

            this.control.valueChanges.subscribe((val) => {
                if (!this.isAmountFocused) {
                    this.displayValue = this.formatIndianNumber(val);
                    this.cdr.markForCheck();
                }
            });
        }
        if (this.isAmount && !this.isAmountFocused) {
            this.displayValue = this.formatIndianNumber(this.control.value);
        }
    }

    /**
     * Formats a number string with Indian comma notation.
     * e.g. 50000 → 50,000 | 100000 → 1,00,000
     */
    formatIndianNumber(val: any): string {
        if (val === null || val === undefined || val === '') return '';
        const num = String(val).replace(/,/g, '');
        if (isNaN(Number(num))) return String(val);
        const parts = num.split('.');
        let intPart = parts[0];
        const decPart = parts.length > 1 ? '.' + parts[1] : '';
        if (intPart.length > 3) {
            const last3 = intPart.slice(-3);
            const rest = intPart.slice(0, -3);
            intPart = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
        }
        return intPart + decPart;
    }

    onAmountFocus(): void {
        this.isAmountFocused = true;
    }

    onAmountBlur(event: Event): void {
        this.isAmountFocused = false;
        const raw = this.displayValue.replace(/,/g, '');
        this.control.setValue(raw, { emitEvent: true });
        this.control.markAsTouched();
        this.displayValue = this.formatIndianNumber(raw);
        this.cdr.markForCheck();
        this.afterBlur.emit(event);
    }

    onAmountInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        // Strip non-digit/decimal chars, keep only one decimal point
        const raw = input.value.replace(/[^0-9.]/g, '');
        const formatted = this.formatIndianNumber(raw);

        // Calculate cursor position accounting for added/removed commas
        const cursorPos = input.selectionStart || 0;
        const commasBefore = (input.value.slice(0, cursorPos).match(/,/g) || [])
            .length;
        const rawBeforeCursor = cursorPos - commasBefore;
        let newCursor = 0;
        let rawCount = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (formatted[i] !== ',') rawCount++;
            if (rawCount > rawBeforeCursor) break;
            newCursor = i + 1;
        }

        this.displayValue = formatted;
        this.control.setValue(raw, { emitEvent: true });
        this.control.markAsDirty();
        this.cdr.markForCheck();

        // Restore cursor position after Angular updates the DOM
        requestAnimationFrame(() => {
            input.value = formatted;
            input.setSelectionRange(newCursor, newCursor);
        });
        this.afterInput.emit(event);
    }

    getErrorMessage(): string {
        const label = this.label
            ? this.translate.instant(this.label)
            : this.translate.instant('thisField');

        if (this.control.hasError('required')) {
            return `${label} ${this.translate.instant('isRequired')}`;
        }

        if (this.control.hasError('nonZeroAmount')) {
            return `${label} ${this.translate.instant('valueCannotBeZero')}`;
        }

        if (this.control.hasError('min')) {
            const min = this.control.getError('min')?.min ?? this.minValue;
            return `${label} ${this.translate.instant('shouldBeGreaterThanOrEqualTo')} ${min}`;
        }

        if (this.control.hasError('max')) {
            const max = this.control.getError('max')?.max ?? this.maxValue;
            return `${label} ${this.translate.instant('shouldBeLessThanOrEqualTo')} ${max}`;
        }

        if (this.control.hasError('minlength')) {
            const requiredLen =
                this.control.getError('minlength')?.requiredLength ??
                this.minLength;
            return `${label} ${this.translate.instant('shouldBeAtLeast')} ${requiredLen} ${this.translate.instant('charactersLong')}`;
        }

        if (this.control.hasError('maxlength')) {
            const requiredLen =
                this.control.getError('maxlength')?.requiredLength ??
                this.maxLength;
            return `${label} ${this.translate.instant('shouldBeAtMost')} ${requiredLen} ${this.translate.instant('charactersLong')}`;
        }

        if (this.control.hasError('pattern')) {
            const requiredPattern = String(
                this.control.getError('pattern')?.requiredPattern ?? ''
            );
            const digitsMatch = /\^\\d\{(\d+)\}\$/.exec(requiredPattern);
            if (digitsMatch) {
                return `${label} ${this.translate.instant('shouldBeOf')} ${digitsMatch[1]} ${this.translate.instant('digits')}`;
            }
            return `${this.translate.instant('invalidFormatFor')} ${label}`;
        }

        return '';
    }
}
